import type { Identifier } from "ra-core";

import type {
  AdvisorAvailabilityBlock,
  OnGuardiaNowEntry,
  ProjectGuardia,
  ProjectGuardiaRosterRow,
  ProjectOfficeHours,
} from "../../types";
import {
  hasDeparted,
  isActiveMember,
  mexicoCityMoment,
  sameId,
  toSeconds,
  type CarouselData,
} from "./carouselShared";

/**
 * Almikia Epic EL2 (TASK-073): guardia validation and the "who is on guardia now" roster, as
 * pure functions. The validation mirrors `public.enforce_project_guardia_valid()` (TASK-070):
 * a guardia is always built FROM the availability the advisor marked, never imposed. The
 * roster is derived from the guardias and the office hours only; no presence or attendance
 * data exists and none is invented.
 */
export type GuardiaValidationError =
  | "weekend"
  | "not_member"
  | "departed"
  | "outside_availability"
  | "overlap"
  | "time_order";

/** The Spanish messages the SQL trigger raises (runtime copy, UI vocabulary). */
export const GUARDIA_ERROR_MESSAGES: Record<GuardiaValidationError, string> = {
  weekend: "Las guardias solo pueden ser de lunes a viernes",
  not_member: "La asesora no es miembro activo del proyecto",
  departed: "No se puede asignar una guardia a una persona dada de baja",
  outside_availability:
    "La guardia queda fuera de la disponibilidad que la asesora marcó",
  overlap: "La asesora ya tiene una guardia que se empalma en ese horario",
  time_order: "La guardia debe terminar después de empezar",
};

export type GuardiaDraft = Pick<
  ProjectGuardia,
  "project_id" | "sales_id" | "guardia_date" | "start_time" | "end_time"
> & { id?: Identifier; ended_at?: string | null };

export interface GuardiaValidationContext
  extends Pick<CarouselData, "salesProjects" | "roster"> {
  sales: readonly {
    id: Identifier;
    advisor_code?: string | null;
    disabled?: boolean;
  }[];
  blocks: readonly Pick<
    AdvisorAvailabilityBlock,
    "sales_id" | "weekday" | "start_time" | "end_time" | "ended_at"
  >[];
  guardias: readonly ProjectGuardia[];
}

/** ISO weekday of a "YYYY-MM-DD" calendar date (1 = Monday .. 7 = Sunday). */
export const isoWeekdayOf = (date: string): number => {
  const [year, month, day] = date.split("-").map(Number);
  const jsDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return jsDay === 0 ? 7 : jsDay;
};

/**
 * Validates a guardia. Returns an error code (`GUARDIA_ERROR_MESSAGES`) or null when it can be
 * saved: Monday-Friday only, an active member who has not left, fully covered by the advisor's
 * marked blocks of that weekday (one block or contiguous / overlapping ones), and not
 * overlapping another non-ended guardia of hers. Ending a guardia is always allowed.
 */
export const validateGuardia = (
  draft: GuardiaDraft,
  context: GuardiaValidationContext,
): GuardiaValidationError | null => {
  if (draft.ended_at) return null;
  const weekday = isoWeekdayOf(draft.guardia_date);
  if (weekday < 1 || weekday > 5) return "weekend";
  if (!isActiveMember(context, draft.sales_id, draft.project_id)) {
    return "not_member";
  }
  const sale = context.sales.find((row) => sameId(row.id, draft.sales_id));
  if (
    !sale ||
    sale.disabled ||
    hasDeparted(
      { id: sale.id, advisor_code: sale.advisor_code },
      context.roster,
    )
  ) {
    return "departed";
  }

  const start = toSeconds(draft.start_time);
  const end = toSeconds(draft.end_time);
  const blocks = context.blocks.filter(
    (block) =>
      sameId(block.sales_id, draft.sales_id) &&
      block.weekday === weekday &&
      !block.ended_at,
  );
  // Walk from the start to the end; the cursor strictly increases, so the loop terminates.
  let cursor = start;
  while (cursor < end) {
    const reach = blocks
      .filter(
        (block) =>
          toSeconds(block.start_time) <= cursor &&
          toSeconds(block.end_time) > cursor,
      )
      .map((block) => toSeconds(block.end_time))
      .sort()
      .pop();
    if (reach === undefined) return "outside_availability";
    cursor = reach;
  }

  const overlaps = context.guardias.some(
    (other) =>
      !other.ended_at &&
      !sameId(other.id, draft.id) &&
      sameId(other.sales_id, draft.sales_id) &&
      other.guardia_date === draft.guardia_date &&
      toSeconds(other.start_time) < end &&
      toSeconds(other.end_time) > start,
  );
  if (overlaps) return "overlap";
  return end > start ? null : "time_order";
};

/**
 * Who is on guardia now: the roster rows of the project whose date is today in Mexico City and
 * whose window contains the current time, while the project is open by its office hours. A
 * project with no office-hours rows at all is treated as always open (display data only); a
 * project that has some is closed on a weekday without a row. Names only, in a stable order.
 */
export const computeOnGuardiaNow = (
  roster: readonly ProjectGuardiaRosterRow[],
  officeHours: readonly ProjectOfficeHours[],
  projectId: Identifier,
  now: Date,
): OnGuardiaNowEntry[] => {
  const moment = mexicoCityMoment(now);
  const projectHours = officeHours.filter((row) =>
    sameId(row.project_id, projectId),
  );
  const isOpen =
    projectHours.length === 0 ||
    projectHours.some(
      (row) =>
        row.weekday === moment.weekday &&
        toSeconds(row.opens_at) <= moment.time &&
        moment.time < toSeconds(row.closes_at),
    );
  if (!isOpen) return [];
  return roster
    .filter(
      (row) =>
        sameId(row.project_id, projectId) &&
        row.guardia_date === moment.date &&
        toSeconds(row.start_time) <= moment.time &&
        moment.time < toSeconds(row.end_time),
    )
    .map((row) => ({
      sales_id: row.sales_id,
      first_name: row.first_name,
      last_name: row.last_name,
    }))
    .sort((a, b) => Number(a.sales_id) - Number(b.sales_id));
};
